import { Link } from "react-router-dom";
import { MapPin, Handshake, MailOpen, Lightbulb, Crown, Sparkles } from "lucide-react";
import { useFetch } from "../lib/hooks";
import { GuideCard } from "./guides/GuidesDirectory";
import { ArticleCard } from "../components/site/ArticleCard";
import { SectionHeading, EmptyState, AdSlot } from "../components/site/Blocks";
import { Seo } from "../components/site/Seo";
import { BrandLoader } from "../components/site/Logo";
import { BRAND } from "../lib/brand";
import { NewsletterForm } from "./Newsletter";
import { WelcomeCard } from "../components/site/WelcomeCard";

const Empty = ({ id }) => <EmptyState compact title="Tekstovi u ovoj rubrici uskoro" text="Redakcija priprema provjerene sadržaje." testId={`empty-${id}`} />;

const Section = ({ id, title, to, items, children, className = "" }) => (items?.length ? (
  <section className={`container-news mt-12 lg:mt-16 ${className}`} data-testid={`home-section-${id}`}>
    <SectionHeading title={title} to={to} id={id} />
    {children}
  </section>
) : null);

const LatestRail = ({ items, pinned = [] }) => (
  <aside aria-labelledby="latest-h" className="lg:border-l lg:border-rule lg:pl-6" data-testid="latest-feed">
    <div className="flex items-center justify-between border-b-2 border-ink pb-2">
      <h2 id="latest-h" className="flex items-center gap-2 font-display text-[1.15rem] font-semibold text-ink">
        <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pulse opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-pulse" /></span>
        Najnovije
      </h2>
      <Link to="/najnovije" className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-pulse hover:underline" data-testid="latest-all-link">Sve →</Link>
    </div>
    {pinned.length > 0 && <ol className="divide-y divide-rule border-b border-rule" data-testid="latest-pinned">{pinned.map((a) => <li key={a.id}><span className="mt-2 block text-[10px] font-extrabold uppercase tracking-[0.12em] text-pulse">Zakačeno</span><ArticleCard a={a} variant="compact" testId={`pinned-${a.slug}`} /></li>)}</ol>}
    {items.length ? (
      <ol className="divide-y divide-rule">{items.map((a) => <li key={a.id}><ArticleCard a={a} variant="compact" testId={`latest-${a.slug}`} /></li>)}</ol>
    ) : <p className="py-4 text-sm text-muted-foreground">Nema novih tekstova.</p>}
  </aside>
);

const Supporting = ({ items, mobile }) =>
  items.map((a, i) => (
    <div key={a.id} className={i && !mobile ? "border-t border-rule pt-5" : ""}>
      <ArticleCard a={a} variant={mobile ? "horizontal" : "standard"} testId={`supporting-${mobile ? "m-" : ""}${i}`} />
    </div>
  ));

const FirstScreen = ({ d }) => (
  <section className="container-news mt-5 lg:mt-8" data-testid="featured-section">
    {d.featured ? (
      <>
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-7 xl:col-span-6"><ArticleCard a={d.featured} variant="hero" testId="article-card-hero" /></div>
          <div className="lg:col-span-5 xl:col-span-6 xl:grid xl:grid-cols-2 xl:gap-6">
            <div className="hidden space-y-6 border-l border-rule pl-6 xl:block" data-testid="supporting-stories"><Supporting items={d.secondary} /></div>
            <LatestRail items={d.latest.slice(0, 6)} pinned={d.pinned || []} />
          </div>
        </div>
        <div className="mt-6 divide-y divide-rule border-t border-rule sm:hidden"><Supporting items={d.secondary} mobile /></div>
        <div className="mt-8 hidden gap-6 border-t border-rule pt-6 sm:grid sm:grid-cols-2 xl:hidden"><Supporting items={d.secondary} /></div>
      </>
    ) : (
      <EmptyState compact title="Vijesti su u pripremi" text="BalkanPuls.de objavljuje samo provjerene tekstove s izvorima. Prve vijesti pojavit će se ovdje čim ih redakcija objavi – do tada pogledaj vodiče ispod." testId="empty-featured" />
    )}
  </section>
);

const Important = ({ items }) =>
  items?.length ? (
    <section className="container-news mt-12 lg:mt-16" data-testid="home-section-vazno-za-vas">
      <div className="grid gap-6 border-y-2 border-ink py-6 lg:grid-cols-12 lg:gap-10 lg:py-8">
        <div className="lg:col-span-3">
          <p className="kicker flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Za tebe</p>
          <h2 className="hl mt-2 text-[1.35rem] leading-tight lg:text-[1.6rem]">Zašto je ovo važno za tebe?</h2>
          <p className="mt-2 text-[13.5px] text-muted-foreground">Kratka objašnjenja redakcije uz objavljene tekstove.</p>
        </div>
        <ol className="grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:col-span-9">
          {items.map((a, i) => (
            <li key={a.id} className="grid grid-cols-[2rem_1fr] gap-3" data-testid={`important-item-${i}`}>
              <span className="font-display text-[1.6rem] font-bold leading-none text-pulse">{String(i + 1).padStart(2, "0")}</span>
              <Link to={`/${a.category?.slug}/${a.slug}`} className="group">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{a.category?.name}</span>
                <h3 className="hl mt-0.5 text-[1.02rem] leading-[1.3]"><span className="headline-link">{a.headline}</span></h3>
                <p className="dek mt-1.5 border-l-2 border-pulse/60 pl-3 text-[13.5px] leading-relaxed text-foreground/80">{a.what_it_means_text}</p>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  ) : null;

const LeadList = ({ items, reverse }) =>
  items.length < 2 ? (
    <ArticleCard a={items[0]} variant="feature" side />
  ) : (
  <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
    <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}><ArticleCard a={items[0]} variant="feature" /></div>
    <div className="divide-y divide-rule lg:col-span-5 lg:-mt-4">{items.slice(1, 5).map((a) => <ArticleCard key={a.id} a={a} variant="horizontal" />)}</div>
  </div>
);

const FourUp = ({ items }) =>
  items.length < 3 ? (
    <div className="grid gap-6 sm:grid-cols-2">{items.map((a) => <ArticleCard key={a.id} a={a} variant="feature" />)}</div>
  ) : (
  <>
    <div className="hidden gap-6 sm:grid sm:grid-cols-2 lg:grid-cols-4">{items.slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} />)}</div>
    <div className="sm:hidden">
      <ArticleCard a={items[0]} />
      <div className="mt-2 divide-y divide-rule">{items.slice(1, 4).map((a) => <ArticleCard key={a.id} a={a} variant="horizontal" testId={`m-${a.slug}`} />)}</div>
    </div>
  </>
);

const Pair = ({ id, title, to, items }) => (items?.length ? (
  <div data-testid={`home-section-${id}`}>
    <SectionHeading title={title} to={to} id={id} />
    <ArticleCard a={items[0]} variant="feature" />
    <div className="mt-2 divide-y divide-rule">{items.slice(1, 3).map((a) => <ArticleCard key={a.id} a={a} variant="horizontal" />)}</div>
  </div>
) : null);

const MostRead = ({ items }) => (
  <aside aria-labelledby="mr-h" className="bg-surface p-5" data-testid="most-read-section">
    <h2 id="mr-h" className="border-b-2 border-ink pb-2 font-display text-[1.15rem] font-semibold text-ink">Najčitanije</h2>
    <ol className="divide-y divide-rule">
      {items.map((a, i) => (
        <li key={a.id} className="flex items-start gap-3 py-3">
          <span className="w-6 shrink-0 font-display text-[1.6rem] font-bold leading-none text-pulse">{i + 1}</span>
          <Link to={`/${a.category?.slug}/${a.slug}`} className="font-display text-[0.98rem] font-semibold leading-snug text-ink hover:text-pulse" data-testid={`most-read-${i + 1}`}>{a.headline}</Link>
        </li>
      ))}
    </ol>
  </aside>
);

const Guides = ({ items }) => (items.length ? (
  <section className="mt-14 bg-navy py-10 text-white lg:mt-20 lg:py-14" data-testid="guides-section">
    <div className="container-news">
      <div className="mb-6 flex items-baseline justify-between border-t border-white/70 pt-3">
        <h2 className="font-display text-[1.3rem] font-semibold sm:text-[1.5rem] lg:text-[1.65rem]">Vodiči za život u Njemačkoj</h2>
        <Link to="/vodici" className="text-[11.5px] font-extrabold uppercase tracking-[0.12em] text-pulse-light hover:underline" data-testid="section-guides-link">Svi vodiči →</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{items.slice(0, 4).map((g) => <GuideCard key={g.slug} g={g} dark />)}</div>
    </div>
  </section>
) : null);

const Intro = () => (
  <section className="bp-ambient border-b border-rule" data-testid="home-intro">
    <div className="container-news flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:py-7">
      <div>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-pulse">{BRAND.slogan}</p>
        <p className="mt-1 font-display text-xl font-semibold text-ink sm:text-2xl">Vijesti, prava i vodiči za život u Njemačkoj – na našem jeziku.</p>
      </div>
      <div className="flex flex-wrap gap-2 text-[13px] font-bold">
        <Link to="/vodici" className="border border-ink px-3.5 py-2 transition-colors hover:bg-ink hover:text-white" data-testid="home-intro-guides">Vodiči</Link>
        <Link to="/formulari" className="border border-rule px-3.5 py-2 transition-colors hover:border-ink" data-testid="home-intro-forms">Formulari</Link>
        <Link to="/pitaj-balkan-ai" className="inline-flex items-center gap-1.5 border border-rule px-3.5 py-2 transition-colors hover:border-ink" data-testid="home-intro-ai"><Sparkles className="h-3.5 w-3.5 text-pulse" /> Pitaj Balkan AI</Link>
      </div>
    </div>
  </section>
);

const PremiumBand = ({ items }) => (items.length ? (
  <section className="bp-ambient bp-ambient-dark mt-12 bg-ink py-10 text-white lg:mt-16" data-testid="home-premium">
    <div className="container-news grid gap-8 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-4">
        <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-pulse-light"><Crown className="h-4 w-4" /> BalkanPuls Premium</p>
        <h2 className="mt-2 font-display text-2xl font-semibold">60 dana besplatno</h2>
        <p className="mt-2 text-sm text-white/75">Od 0,99 € mjesečno nakon probnog perioda. Nema automatske naplate.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/premium" className="bg-pulse px-4 py-2.5 text-sm font-bold text-white transition-transform duration-150 hover:-translate-y-0.5" data-testid="home-premium-trial">Aktiviraj 60 dana besplatno</Link>
          <Link to="/vodici?pristup=PREMIUM" className="border border-white/30 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white/10" data-testid="home-premium-guides">Premium vodiči</Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3 lg:col-span-8">{items.slice(0, 3).map((g) => <GuideCard key={g.slug} g={g} dark />)}</div>
    </div>
  </section>
) : null);

const Regions = ({ regions }) => (
  <section className="container-news mt-12 lg:mt-16" data-testid="regions-section">
    <SectionHeading title="Njemačka po pokrajinama" id="regions" />
    <ul className="flex flex-wrap gap-2">
      {regions.map((r) => (
        <li key={r.slug}>
          <Link to={`/vijesti?regija=${r.slug}`} lang="de" className="inline-flex items-center gap-1.5 rounded-full border border-rule px-3 py-1.5 text-[13px] font-semibold text-foreground transition-colors hover:border-pulse hover:text-pulse" data-testid={`region-link-${r.slug}`}>
            <MapPin className="h-3 w-3 text-pulse" aria-hidden />{r.name}{r.count > 0 && <span className="text-muted-foreground">· {r.count}</span>}
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

const PartnersNewsletter = () => (
  <section className="container-news mt-12 grid gap-6 lg:mt-16 lg:grid-cols-2" data-testid="home-section-partneri">
    <div className="border border-rule p-6 lg:p-8" data-testid="partner-spotlight">
      <p className="kicker flex items-center gap-2"><Handshake className="h-4 w-4" /> Partneri i usluge</p>
      <h2 className="hl mt-3 text-[1.35rem]">Partneri iz zajednice</h2>
      <p className="mt-2 text-[14px] text-muted-foreground">Imenik firmi i usluga za našu zajednicu u Njemačkoj. Svaki profil pregleda tim prije objave, a plaćene pozicije su jasno označene.</p>
      <div className="mt-4 flex gap-4"><Link to="/partneri" className="text-[13px] font-bold text-pulse hover:underline" data-testid="partner-spotlight-link">Imenik partnera →</Link><Link to="/postani-partner" className="text-[13px] font-bold hover:underline" data-testid="partner-spotlight-join">Postanite partner →</Link></div>
    </div>
    <div className="bg-surface p-6 lg:p-8" data-testid="newsletter-module">
      <p className="kicker flex items-center gap-2"><MailOpen className="h-4 w-4" /> Newsletter</p>
      <h2 className="hl mt-3 text-[1.35rem]">Sedmični pregled promjena koje su važne</h2>
      <p className="mt-2 text-[14px] text-muted-foreground">Jednom sedmično: najvažnije promjene o boravku, poslu, porezima i porodici. Prijava uz potvrdu putem emaila.</p>
      <NewsletterForm />
    </div>
  </section>
);

export default function Home() {
  const { data, loading } = useFetch("/public/home");
  const gd = useFetch("/guides/directory");
  if (loading) return <BrandLoader />;
  const gAll = gd.data?.items || [];
  const gFree = gAll.filter((g) => g.access !== "PREMIUM");
  const gPrem = gAll.filter((g) => g.access === "PREMIUM");
  const d = data || { latest: [], secondary: [], most_read: [], sections: {}, regions: [] };
  const s = d.sections || {};
  const more = d.latest.slice(6, 10);
  const jsonLd = { "@context": "https://schema.org", "@type": "NewsMediaOrganization", name: BRAND.name, url: "https://balkanpuls.de", logo: "https://balkanpuls.de/brand/icon-512.png", email: BRAND.email, slogan: BRAND.slogan };
  return (
    <div data-testid="home-page">
      <Seo jsonLd={jsonLd} />
      <h1 className="sr-only">BalkanPuls.de – vijesti i vodiči za život u Njemačkoj</h1>
      <Intro />
      <FirstScreen d={d} />
      {!d.featured && <Guides items={gAll} />}
      <div className="container-news"><AdSlot slot="homepage_top" /></div>
      {more.length > 0 && <Section id="najnovije-vijesti" title="Najnovije vijesti" to="/najnovije" items={more}><FourUp items={more} /></Section>}
      <WelcomeCard />
      <div className="container-news"><AdSlot slot="homepage_feed" media="(min-width: 768px)" /><AdSlot slot="mobile_feed" media="(max-width: 767px)" /></div>
      <Important items={d.important} />
      <Section id="vize-boravak" title="Vize i boravak" to="/vize-boravak" items={s["vize-boravak"]}><LeadList items={s["vize-boravak"] || []} /></Section>
      <section className="container-news mt-12 grid gap-10 lg:mt-16 lg:grid-cols-2 lg:gap-8">
        <Pair id="plate-porezi" title="Plate i porezi" to="/plate-porezi" items={s["plate-porezi"]} />
        <Pair id="posao" title="Posao i radnička prava" to="/posao" items={s.posao} />
      </section>
      <Section id="porodica" title="Porodica i djeca" to="/porodica" items={s.porodica}><FourUp items={s.porodica || []} /></Section>
        {null}
      <section className="container-news mt-12 grid gap-8 lg:mt-16 lg:grid-cols-12" data-testid="home-section-zivot-u-njemackoj" hidden={!s["zivot-u-njemackoj"]?.length && !d.most_read.length}>
        <div className="lg:col-span-8">
          {s["zivot-u-njemackoj"]?.length > 0 && <><SectionHeading title="Život u Njemačkoj" to="/zivot-u-njemackoj" id="zivot-u-njemackoj" /><ArticleCard a={s["zivot-u-njemackoj"][0]} variant="feature" side /></>}
          <div className="mt-2 grid gap-x-8 sm:grid-cols-2">{(s["zivot-u-njemackoj"] || []).slice(1, 5).map((a) => <ArticleCard key={a.id} a={a} variant="horizontal" />)}</div>
        </div>
        <div className="lg:col-span-4">{d.most_read.length > 0 && <MostRead items={d.most_read} />}</div>
      </section>
      <Section id="zakoni-prava" title="Važni zakoni i promjene" to="/zakoni-prava" items={s["zakoni-prava"]}>
        <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">{(s["zakoni-prava"] || []).slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} variant="text" />)}</div>
      </Section>
      <Section id="balkan-dijaspora" title="Balkan i dijaspora" to="/balkan-dijaspora" items={s["balkan-dijaspora"]}>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8"><ArticleCard a={(s["balkan-dijaspora"] || [])[0]} variant="feature" side /></div>
          <div className="space-y-5 lg:col-span-4">{(s["balkan-dijaspora"] || []).slice(1, 3).map((a) => <ArticleCard key={a.id} a={a} variant="text" />)}</div>
        </div>
      </Section>
      {d.featured && <Guides items={gFree.length ? gFree : gAll} />}
      <PremiumBand items={gPrem} />
      <Regions regions={d.regions} />
      <PartnersNewsletter />
    </div>
  );
}
